import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createContactTagV1,
  deleteContactTagV1,
  retrieveContactTagsV1,
  updateContactTagV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type ContactTag } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import dayjs from "dayjs";
import { orderBy } from "lodash";
import toast from "react-hot-toast";

export const retrieveContactTagQueryOptions = () => {
  return {
    queryKey: [queryKeys.contactTags],
    queryFn: async () => retrieveContactTagsV1(),
  };
};

export const useCreateContactTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactTagV1>[0]) =>
      createContactTagV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contactTags],
        (oldData: APIResponse<ContactTag[]> | undefined) => {
          if (!oldData) return;
          return {
            data: orderBy(
              [
                ...oldData.data,
                { dateCreated: dayjs().toISOString(), ...data },
              ],
              ["label"],
              ["asc"],
            ),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.contactTags]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.contactTags], context.previousData);
      }
      toast.error("Error creating contact tag");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.contactTags],
      });
    },
    onSuccess: () => {
      toast.success("Contact tag created successfully");
    },
  });
};

export const useUpdateContactTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactTagV1>[0]) =>
      updateContactTagV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contactTags],
        (oldData: APIResponse<ContactTag[]> | undefined) => {
          if (!oldData) return;
          return {
            data: orderBy(
              oldData.data.map((tag) => {
                return tag.id === data.id ? { ...tag, ...data } : tag;
              }),
              ["label"],
              ["asc"],
            ),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.contactTags]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.contactTags], context.previousData);
      }
      toastAPIErrorMessage(error, "Error updating contact tag");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.contactTags],
      });
    },
    onSuccess: () => {
      toast.success("Contact tag updated successfully");
    },
  });
};

export const useDeleteContactTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactTagV1>[0]) =>
      deleteContactTagV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contactTags],
        (oldData: APIResponse<ContactTag[]> | undefined) => {
          if (!oldData) return;
          return {
            data: oldData.data.filter((tag) => tag.id !== data.id),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.contactTags]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([queryKeys.contactTags], context.previousData);
      }
      toast.error("Error deleting contact tag");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.contactTags],
      });
    },
    onSuccess: () => {
      toast.success("Contact tag deleted successfully");
    },
  });
};
